import type { Sheet } from './import-csv';

/**
 * Reading an Excel workbook, so nobody has to save it as CSV first — #368.
 *
 * WHY THIS EXISTS. The importer read CSV only, and the screen told people to go
 * back into Excel and export one. TRINRE — and the projects we are starting to
 * track — keep their work in Excel, so every import began with a save-as step
 * that loses formatting and invites mistakes. This takes the .xlsx directly.
 *
 * IT PRODUCES A `Sheet`, THE SAME ONE parseCsv DOES, and stops there. Everything
 * downstream — the column mapping, the word mapping, the plan, the writing — is
 * about a `Sheet` and has no idea where it came from. So this file is only the
 * reading; not a line of the rest changes.
 *
 * WHY SheetJS RATHER THAN OUR OWN. The CSV parser is hand-written on purpose,
 * but an .xlsx is a zip of XML with shared strings, cached values, number
 * formats and dates — a great deal to get subtly wrong on files that arrive from
 * every version of Excel there is. Vinnie chose the library on 7 September 2026.
 * It is loaded on demand — `import('xlsx')` below — so it costs nothing until
 * somebody actually brings an Excel file, and the CSV path never pays for it.
 *
 * VALUES, NOT FORMULAE. `cellFormula: false` and `raw: false` together mean each
 * cell is read as the text Excel last showed — the computed value of a formula,
 * a date in the format it was written, a number as it appeared — which is
 * exactly what a CSV export of the same sheet would have contained.
 */

/** What a read gives back: the rows as a Sheet, plus which sheet and all of them. */
export type Workbook = Sheet & { source: 'xlsx'; sheetName: string; sheetNames: string[] };

export async function parseXlsx(data: ArrayBuffer, want?: string): Promise<Workbook> {
  // Loaded here, not at the top, so the library only downloads when it is used.
  // The interop guard covers both the CommonJS build and any ESM wrapper of it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mod = (await import('xlsx')) as any;
  const XLSX = (mod.read ? mod : mod.default) as typeof import('xlsx');

  const wb = XLSX.read(data, { type: 'array', cellDates: true, cellFormula: false });
  const sheetNames = wb.SheetNames.slice();

  /*
   * WHICH SHEET. The one asked for if it is really there; otherwise the first
   * one that has more than a heading row, because a workbook's first sheet is
   * often a cover or an instructions page and the backlog is the second tab.
   * Falls back to the very first sheet so an odd workbook still shows something
   * rather than nothing.
   */
  const hasRows = (name: string): boolean => {
    const ref = wb.Sheets[name]?.['!ref'];
    if (!ref) return false;
    const range = XLSX.utils.decode_range(ref);
    return range.e.r > range.s.r; // at least two rows: a header and one under it
  };
  const sheetName =
    (want && sheetNames.includes(want) && want) ||
    sheetNames.find(hasRows) ||
    sheetNames[0] ||
    '';

  const ws = sheetName ? wb.Sheets[sheetName] : undefined;
  const aoa: string[][] = ws
    ? (XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, raw: false, defval: '' }) as unknown[][]).map(
        (r) => r.map((c) => (c == null ? '' : String(c))),
      )
    : [];

  if (aoa.length === 0) {
    return { headers: [], rows: [], delimiter: ',', ragged: [], source: 'xlsx', sheetName, sheetNames };
  }

  // Headers and rows exactly as parseCsv shapes them, so the two are interchangeable:
  // a blank heading is still nameable, and every row is padded to the header width.
  const headers = aoa[0].map((h, i) => h.trim() || `Column ${i + 1}`);
  const ragged: number[] = [];
  const rows = aoa.slice(1).map((r, i) => {
    if (r.length !== headers.length) ragged.push(i + 2); // +2: 1-based, past the header
    const out = r.slice(0, headers.length);
    while (out.length < headers.length) out.push('');
    return out;
  });

  return { headers, rows, delimiter: ',', ragged, source: 'xlsx', sheetName, sheetNames };
}

/** Does this file look like an Excel workbook, by name or by the type the browser gave it? */
export function looksLikeExcel(file: File): boolean {
  return (
    /\.xlsx?$/i.test(file.name) ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel'
  );
}
